'use client';

import React from 'react';
import Image from 'next/image';
import { LocalizedLink } from './LocalizedLink';
import { useTranslations } from '@/hooks/useTranslations';
import { getCasinoCardDescription, getCasinoLogoAlt } from '@/lib/casino-catalog';
import { CasinoLogoPlaceholder } from '@/components/ThemedPlaceholders';
import { StarRatingBar } from '@/components/StarRating';

type Casino = {
  id: string;
  name: string;
  slug: string;
  shortDescription: string;
  computedRating: number | string;
  /** Number of user reviews; stars and rating are hidden when 0 */
  ratingCount?: number;
  bonusShort: string;
  paymentMethods: string[];
  logoUrl?: string;
};

export function CasinoCard({
  casino,
  pageTitle,
  imageNumber = 1,
  compact = false,
}: {
  casino: Casino;
  pageTitle?: string;
  imageNumber?: number;
  compact?: boolean;
}) {
  const { t } = useTranslations();
  const [logoFailed, setLogoFailed] = React.useState(false);
  const hasReviews = (casino.ratingCount ?? 0) > 0;
  const rating = Number(casino.computedRating);
  const safeRating = Number.isFinite(rating) ? Math.max(0, Math.min(5, rating)) : 0;
  const cardDescription = getCasinoCardDescription(casino);
  const logoAlt = getCasinoLogoAlt(casino.name);
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('') || 'C';
  };

  return (
    <div className={`${compact ? 'rounded-2xl p-4 sm:p-5' : 'rounded-3xl p-6'} border border-white/10 bg-[#1e2235]/80 shadow-[0_12px_30px_rgba(7,9,20,0.45)] transition-shadow hover:shadow-[0_18px_38px_rgba(7,9,20,0.55)]`}>
      <div className={`${compact ? 'flex flex-col gap-4 md:flex-row md:items-center md:justify-between' : 'flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'}`}>
        <div className={`${compact ? 'flex items-start gap-4 md:w-[38%]' : 'flex items-start gap-4 lg:w-1/3'}`}>
          <div className={`${compact ? 'h-20 w-20 sm:h-24 sm:w-24' : 'h-24 w-24 sm:h-28 sm:w-28 lg:h-32 lg:w-32'} flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-[#15192b]`}>
            {casino.logoUrl && !logoFailed ? (
              <Image
                src={casino.logoUrl}
                alt={logoAlt}
                title={casino.name}
                width={192}
                height={192}
                sizes="192px"
                className="h-full w-full object-contain p-2"
                loading="lazy"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <CasinoLogoPlaceholder initials={getInitials(casino.name)} />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white">
              <LocalizedLink
                href={`/casinos/${casino.slug}`}
                className="hover:text-[#ffb74d]"
              >
                {casino.name}
              </LocalizedLink>
            </h3>
            {hasReviews ? (
              <div className="mt-1 flex items-center gap-2">
                <StarRatingBar rating={safeRating} />
                <span className="text-xs font-medium text-slate-300" aria-hidden>
                  {Number.isFinite(rating) ? rating.toFixed(1) : '—'}
                </span>
              </div>
            ) : (
              <p className="mt-1 text-xs text-slate-500">{t('casinosPage.noReviewsYet')}</p>
            )}
            {cardDescription ? (
              <p className="mt-1 text-sm text-slate-400">{cardDescription}</p>
            ) : null}
          </div>
        </div>
        <div className={`${compact ? 'md:max-w-sm' : ''} flex-1 rounded-2xl border border-white/5 bg-white/5 px-4 py-3 text-sm text-slate-300`}>
          <span className="text-xs uppercase tracking-wider text-slate-500">
            {t('casino.welcomeBonus')}
          </span>
          <div className={`${compact ? 'text-base' : 'text-lg'} mt-1 line-clamp-2 font-semibold text-[#ffb74d]`}>
            {casino.bonusShort}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {casino.paymentMethods.slice(0, compact ? 3 : 4).map((method) => (
              <span
                key={method}
                className="rounded-full border border-white/10 px-2 py-1 text-xs text-slate-300"
              >
                {method}
              </span>
            ))}
          </div>
        </div>
        <div className={`${compact ? 'md:items-end' : 'lg:items-end'} flex flex-col items-start gap-3`}>
          <LocalizedLink
            href={`/casinos/${casino.slug}`}
            className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#ffb74d] px-5 py-2 text-xs font-semibold text-[#4a2800] transition hover:bg-amber-400"
            aria-label={t('casino.viewCasinoName', { name: casino.name })}
          >
            {t('casino.viewCasino')}
          </LocalizedLink>
        </div>
      </div>
    </div>
  );
}
